import { useRouter } from 'expo-router';
import { useId, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, ICONS } from './icon';
import { collectionsOfKind } from '../lib/collections';
import { localePath } from '../lib/format';
import { SITE_UI } from '@site-ui';
import { matchesSearch, normalizeSearch, searchWords } from '../lib/search';
import { useLocale } from '@todo/core/i18n/use-locale';

import css from './hero-search.module.css';

/** A popular search: what the row says, and the `?q=` it runs (checked to find tours). */
type PopularSearch = { label: string; q: string };

/** One row of the suggestion list. */
type Suggestion = { id: string; label: string; href: string; kind: 'query' | 'category' | 'popular' };

/** Rows per group, so both groups show without a query. */
const MAX_CATEGORIES = 4;
const MAX_POPULAR = 6;

// The home page hero: full-bleed photo, headline, lead and the search — one
// pill (query | search button). A search opens the tours page with `?q=`,
// which filters its list; without JS the form submits there too. The design's
// "When?" field is left out: the tours have no availability by date to filter.
//
// While the field has focus, a list of suggestions opens under the pill: the
// category pages and the popular searches (`search.popular` in the resources),
// filtered by what's typed, plus "Search for …" once there's a query. A
// combobox in the ARIA sense: arrows move through it, Enter opens the row,
// Escape closes it. Closed on the first render, so hydration matches.
export function HeroSearch() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const router = useRouter();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const action = localePath(locale, 'tours');
  const searchHref = (q: string) => `${action}?q=${encodeURIComponent(q)}`;

  const words = searchWords(query);
  const matching = (label: string) => !words.length || matchesSearch(normalizeSearch(label), words);
  const categories: Suggestion[] = collectionsOfKind('category')
    .map((c) => ({ id: c.slug, label: t(`collections.${c.slug}.title`), href: localePath(locale, c.slug) }))
    .filter((c) => matching(c.label))
    .slice(0, MAX_CATEGORIES)
    .map((c) => ({ ...c, kind: 'category' }));
  const popularList = t('search.popular', { returnObjects: true }) as unknown;
  const popular: Suggestion[] = (Array.isArray(popularList) ? (popularList as PopularSearch[]) : [])
    .filter((p) => matching(p.label) || matching(p.q))
    .slice(0, MAX_POPULAR)
    .map((p) => ({ id: `q-${p.q}`, label: p.label, href: searchHref(p.q), kind: 'popular' }));
  const typed = query.trim();
  const suggestions: Suggestion[] = [
    ...(typed ? [{ id: 'query', label: t('search.searchFor', { query: typed }), href: searchHref(typed), kind: 'query' as const }] : []),
    ...categories,
    ...popular,
  ];
  const expanded = open && suggestions.length > 0;

  const go = (href: string) => {
    setOpen(false);
    router.push(href as never);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const chosen = expanded && active >= 0 ? suggestions[active] : undefined;
    go(chosen ? chosen.href : typed ? searchHref(typed) : action);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      // -1 is the field itself: the cycle runs field → rows → field.
      const slots = suggestions.length + 1;
      setActive((i) => ((i + 1 + step + slots) % slots) - 1);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  // An optional line over the headline (the miami design's "Your Miami guide").
  const eyebrow = t('home.heroEyebrow', { defaultValue: '' });

  const section = (kind: Suggestion['kind']) =>
    kind === 'category' ? t('search.categories') : kind === 'popular' ? t('search.popularTitle') : null;

  return (
    <section aria-labelledby="hero-title" className={css.hero}>
      {/* The LCP element: eager and high priority. */}
      <img
        className={css.photo}
        src={SITE_UI.hero.image}
        srcSet={SITE_UI.hero.srcSet}
        sizes="100vw"
        width={SITE_UI.hero.width}
        height={SITE_UI.hero.height}
        alt={t('home.heroAlt')}
        fetchPriority="high"
        decoding="async"
      />
      <div className={css.inner}>
        {eyebrow ? <p className={css.eyebrow}>{eyebrow}</p> : null}
        <h1 id="hero-title" className={css.title}>
          {t('home.heroTitle')}
        </h1>
        <p className={css.lead}>{t('home.heroLead')}</p>
        <div className={css.searchBox}>
          <form role="search" aria-label={t('search.landmark')} action={action} onSubmit={onSubmit} className={css.form}>
            <label className={css.field}>
              <span className="visually-hidden">{t('search.label')}</span>
              <input
                type="search"
                name="q"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOpen(true);
                  setActive(-1);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onKeyDown={onKeyDown}
                placeholder={t('search.placeholder')}
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={expanded}
                aria-controls={listId}
                aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
                className={css.input}
              />
            </label>
            <button type="submit" className={css.submit}>
              <Icon d={ICONS.search} size={18} strokeWidth={2} className={css.submitIcon} />
              <span className={css.submitLabel}>{t('search.submit')}</span>
            </button>
          </form>

          <ul id={listId} role="listbox" aria-label={t('search.suggestions')} className={css.suggestions} hidden={!expanded}>
            {suggestions.map((s, i) => (
              <li
                key={s.id}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                data-section={i === 0 || suggestions[i - 1].kind !== s.kind ? section(s.kind) ?? undefined : undefined}
                className={css.suggestion}
                // mousedown, not click: the input's blur would close the list first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  go(s.href);
                }}
                onMouseEnter={() => setActive(i)}>
                <Icon d={s.kind === 'category' ? ICONS.arrowLong : ICONS.search} size={16} className={css.suggestionIcon} />
                {s.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
